import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  AlertTriangle, Trophy, Target, Loader2, ArrowLeft, HelpCircle
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const AssessmentTakePage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assessment, setAssessment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("intro"); // intro | quiz | result
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [aRes, attRes] = await Promise.all([
          api.get(`/assessments/${assessmentId}`),
          api.get(`/assessments/${assessmentId}/attempts`)
        ]);
        setAssessment(aRes.data);
        setAttempts(attRes.data || []);
      } catch (e) {
        toast.error("Failed to load assessment");
        console.error(e);
      } finally { setLoading(false); }
    };
    fetchData();
  }, [assessmentId]);

  const startQuiz = () => {
    if (!assessment) return;
    let qs = [...(assessment.questions || [])];
    if (assessment.shuffle_questions) {
      qs = qs.sort(() => Math.random() - 0.5);
    }
    setQuestions(qs);
    setAnswers({});
    setCurrentQ(0);
    setTimeLeft(assessment.time_limit_minutes * 60);
    startTimeRef.current = Date.now();
    setPhase("quiz");
  };

  // Timer
  useEffect(() => {
    if (phase !== "quiz" || timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = (qId, answer) => {
    setAnswers(prev => ({ ...prev, [qId]: answer }));
  };

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
    try {
      const res = await api.post(`/assessments/${assessmentId}/submit`, {
        answers,
        time_spent_seconds: timeSpent
      });
      setResult(res.data);
      setPhase("result");
      // Refresh attempts
      const attRes = await api.get(`/assessments/${assessmentId}/attempts`);
      setAttempts(attRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); setShowConfirm(false); }
  }, [assessmentId, answers, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const answeredCount = Object.keys(answers).length;
  const totalQ = questions.length;
  const canRetry = assessment && attempts.length < assessment.max_attempts;
  const bestScore = attempts.length > 0 ? Math.max(...attempts.map(a => a.score)) : null;

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!assessment) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground">Assessment not found</p>
    </div>
  );

  // INTRO PHASE
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto p-6 space-y-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>

          <Card>
            <CardContent className="p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <HelpCircle className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="assessment-take-title">
                  {assessment.title}
                </h1>
                {assessment.description && (
                  <p className="text-muted-foreground max-w-md mx-auto">{assessment.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl border text-center">
                  <HelpCircle className="w-5 h-5 text-primary mx-auto mb-1" />
                  <p className="text-xl font-bold">{assessment.questions?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Questions</p>
                </div>
                <div className="p-4 rounded-xl border text-center">
                  <Clock className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                  <p className="text-xl font-bold">{assessment.time_limit_minutes}</p>
                  <p className="text-xs text-muted-foreground">Minutes</p>
                </div>
                <div className="p-4 rounded-xl border text-center">
                  <Target className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-xl font-bold">{assessment.passing_score}%</p>
                  <p className="text-xs text-muted-foreground">Pass Score</p>
                </div>
                <div className="p-4 rounded-xl border text-center">
                  <Trophy className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                  <p className="text-xl font-bold">{attempts.length}/{assessment.max_attempts}</p>
                  <p className="text-xs text-muted-foreground">Attempts Used</p>
                </div>
              </div>

              {/* Previous Attempts */}
              {attempts.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">Previous Attempts</h3>
                  {attempts.map((att, i) => (
                    <div key={att.id} className={`flex items-center justify-between p-3 rounded-lg border ${att.passed ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                      <div className="flex items-center gap-2">
                        {att.passed ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                        <span className="text-sm font-medium">Attempt {att.attempt_number}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm">{Math.floor(att.time_spent_seconds / 60)}m {att.time_spent_seconds % 60}s</span>
                        <Badge className={att.passed ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}>
                          {att.score}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {bestScore !== null && (
                    <p className="text-sm text-muted-foreground">Best score: <span className="font-semibold text-foreground">{bestScore}%</span></p>
                  )}
                </div>
              )}

              <Button
                className="w-full h-12 text-base"
                onClick={startQuiz}
                disabled={!canRetry}
                data-testid="start-quiz-btn"
              >
                {!canRetry ? "Maximum Attempts Reached" : attempts.length > 0 ? "Retry Assessment" : "Start Assessment"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // QUIZ PHASE
  if (phase === "quiz") {
    const q = questions[currentQ];
    const isWarning = timeLeft <= 60;

    return (
      <div className="min-h-screen bg-background">
        {/* Timer Bar */}
        <div className={`sticky top-0 z-50 px-6 py-3 border-b flex items-center justify-between ${isWarning ? "bg-red-500/5 border-red-500/20" : "bg-background/95 backdrop-blur-sm"}`}>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium" data-testid="question-counter">
              Q {currentQ + 1} / {totalQ}
            </span>
            <Progress value={(answeredCount / totalQ) * 100} className="w-32 h-2" />
            <span className="text-xs text-muted-foreground">{answeredCount}/{totalQ} answered</span>
          </div>
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-1.5 font-mono text-sm font-bold ${isWarning ? "text-red-500" : "text-foreground"}`} data-testid="quiz-timer">
              <Clock className="w-4 h-4" />
              {formatTime(timeLeft)}
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowConfirm(true)}
              data-testid="submit-quiz-btn"
            >
              Submit
            </Button>
          </div>
        </div>

        {/* Question Navigation */}
        <div className="border-b bg-muted/30 px-6 py-2">
          <div className="flex gap-1.5 flex-wrap max-w-4xl mx-auto">
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQ(i)}
                className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                  i === currentQ
                    ? "bg-primary text-primary-foreground"
                    : answers[questions[i]?.id]
                    ? "bg-green-500/20 text-green-700 border border-green-500/30"
                    : "bg-background border hover:bg-muted"
                }`}
                data-testid={`q-nav-${i}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question Content */}
        <div className="max-w-3xl mx-auto p-6">
          <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
            <Card>
              <CardContent className="p-8 space-y-6">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className="shrink-0 mt-0.5">
                    {q.question_type === "mcq" ? "MCQ" : q.question_type === "true_false" ? "T/F" : "Fill-in"}
                  </Badge>
                  <div>
                    <h3 className="text-lg font-semibold" data-testid="question-text">{q.question_text}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{q.points} point{q.points > 1 ? "s" : ""}</p>
                  </div>
                </div>

                {/* MCQ Options */}
                {q.question_type === "mcq" && (
                  <div className="space-y-2">
                    {q.options?.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => handleAnswer(q.id, opt.toLowerCase())}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          answers[q.id] === opt.toLowerCase()
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/30 hover:bg-muted/30"
                        }`}
                        data-testid={`option-${oi}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                            answers[q.id] === opt.toLowerCase()
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-muted-foreground/30"
                          }`}>
                            {String.fromCharCode(65 + oi)}
                          </div>
                          <span className="font-medium text-sm">{opt}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* True/False */}
                {q.question_type === "true_false" && (
                  <div className="grid grid-cols-2 gap-3">
                    {["true", "false"].map(val => (
                      <button
                        key={val}
                        onClick={() => handleAnswer(q.id, val)}
                        className={`p-5 rounded-xl border-2 text-center font-semibold transition-all ${
                          answers[q.id] === val
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/30"
                        }`}
                        data-testid={`option-${val}`}
                      >
                        {val.charAt(0).toUpperCase() + val.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Fill in the blank */}
                {q.question_type === "fill_in" && (
                  <Input
                    value={answers[q.id] || ""}
                    onChange={e => handleAnswer(q.id, e.target.value.toLowerCase())}
                    placeholder="Type your answer..."
                    className="h-12 text-base"
                    data-testid="fill-in-input"
                  />
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQ(prev => Math.max(0, prev - 1))}
                    disabled={currentQ === 0}
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  {currentQ < totalQ - 1 ? (
                    <Button
                      onClick={() => setCurrentQ(prev => Math.min(totalQ - 1, prev + 1))}
                      data-testid="next-question-btn"
                    >
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowConfirm(true)}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="finish-quiz-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Finish
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Submit Confirmation */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Submit Assessment?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                You have answered <span className="font-semibold text-foreground">{answeredCount}</span> out of <span className="font-semibold text-foreground">{totalQ}</span> questions.
              </p>
              {answeredCount < totalQ && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700">{totalQ - answeredCount} question(s) unanswered</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Review</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // RESULT PHASE
  if (phase === "result" && result) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto p-6 space-y-6">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <Card className={result.passed ? "border-green-500/30" : "border-red-500/30"}>
              <CardContent className="p-8 text-center space-y-6">
                <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${result.passed ? "bg-green-500/10" : "bg-red-500/10"}`}>
                  {result.passed
                    ? <CheckCircle2 className="w-10 h-10 text-green-500" />
                    : <XCircle className="w-10 h-10 text-red-500" />}
                </div>

                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="result-title">
                    {result.passed ? "Congratulations!" : "Not Quite Yet"}
                  </h2>
                  <p className="text-muted-foreground mt-1">
                    {result.passed
                      ? "You passed the assessment!"
                      : `You need ${assessment.passing_score}% to pass. Keep studying!`}
                  </p>
                </div>

                <div className="text-6xl font-bold" data-testid="result-score">
                  <span className={result.passed ? "text-green-500" : "text-red-500"}>{result.score}%</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 rounded-xl border">
                    <p className="font-bold">{result.earned_points}/{result.total_points}</p>
                    <p className="text-xs text-muted-foreground">Points</p>
                  </div>
                  <div className="p-3 rounded-xl border">
                    <p className="font-bold">{assessment.passing_score}%</p>
                    <p className="text-xs text-muted-foreground">Required</p>
                  </div>
                  <div className="p-3 rounded-xl border">
                    <p className="font-bold">#{result.attempt_number}</p>
                    <p className="text-xs text-muted-foreground">Attempt</p>
                  </div>
                </div>

                {/* Show answers with explanations */}
                <div className="text-left space-y-3 pt-4 border-t">
                  <h3 className="font-semibold text-sm">Answer Review</h3>
                  {questions.map((q, i) => {
                    const userAns = answers[q.id] || "";
                    const correct = q.correct_answer?.toLowerCase().trim();
                    const isCorrect = userAns.trim().toLowerCase() === correct;
                    return (
                      <div key={i} className={`p-3 rounded-lg border ${isCorrect ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`} data-testid={`review-q-${i}`}>
                        <div className="flex items-start gap-2">
                          {isCorrect
                            ? <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{q.question_text}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs">
                              <span>Your answer: <span className={`font-semibold ${isCorrect ? "text-green-600" : "text-red-600"}`}>{userAns || "(blank)"}</span></span>
                              {!isCorrect && <span>Correct: <span className="font-semibold text-green-600">{correct}</span></span>}
                            </div>
                            {q.explanation && <p className="text-xs text-muted-foreground mt-1">{q.explanation}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => navigate(-1)} data-testid="back-to-list-btn">
                    Back
                  </Button>
                  {canRetry && !result.passed && (
                    <Button className="flex-1" onClick={() => { setPhase("intro"); }} data-testid="retry-btn">
                      Try Again
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default AssessmentTakePage;
